/**
 * Reading a `text/event-stream`, on a phone and in a browser.
 *
 * Neither of the obvious tools works for an authenticated stream. The
 * browser's `EventSource` cannot set an `Authorization` header, and putting
 * an access token in the query string would write it into every proxy log
 * between here and the customer. React Native has no `EventSource` at all,
 * and its `fetch` cannot stream a response body — `response.body` is not a
 * `ReadableStream` there, so the whole answer arrives at once, which for a
 * stream means never.
 *
 * `XMLHttpRequest` is what both of them do have. It takes headers like any
 * other request, and it reports `responseText` as the bytes arrive, so the
 * parser below reads the part it has not seen yet each time. That is the
 * entire trick, and it is why this is a hundred lines rather than three.
 */
/**
 * Opens a stream and returns the function that closes it.
 *
 * Calling that function is the only way it ends from this side, and it is
 * safe to call twice — `onClose` fires once either way, so a caller can
 * tear down in a `useEffect` cleanup without guarding it.
 */
export function openEventStream(url, headers, handlers) {
    const Progressive = globalThis.XMLHttpRequest;
    if (!Progressive) {
        // A server rendering a page, or a test runner. Nobody is watching a
        // stream there, so this is a no-op rather than a crash.
        handlers.onClose(new Error('Streaming is not available in this runtime'));
        return () => undefined;
    }
    const request = new Progressive();
    let read = 0;
    let done = false;
    const finish = (error) => {
        if (done)
            return;
        done = true;
        handlers.onClose(error);
    };
    /*
     * Everything arrived since the last time, cut into events on the blank
     * line that separates them. The tail after the last blank line is a
     * partial event still being written, so it is left in place and picked up
     * on the next progress — which is what makes this safe against a message
     * split across two packets.
     */
    const drain = () => {
        const text = request.responseText;
        if (text.length <= read)
            return;
        const fresh = text.slice(read);
        const chunks = fresh.split('\n\n');
        // The last piece is either empty or half an event. Either way it waits.
        read += fresh.length - chunks[chunks.length - 1].length;
        for (const chunk of chunks.slice(0, -1)) {
            const event = parseEvent(chunk);
            if (event)
                handlers.onEvent(event);
        }
    };
    request.open('GET', url, true);
    for (const [name, value] of Object.entries(headers))
        request.setRequestHeader(name, value);
    request.setRequestHeader('Accept', 'text/event-stream');
    request.onprogress = drain;
    request.onload = () => {
        drain();
        finish();
    };
    request.onerror = () => finish(new Error('The stream could not be reached'));
    request.ontimeout = () => finish(new Error('The stream timed out'));
    request.onabort = () => finish();
    try {
        request.send();
    }
    catch (error) {
        finish(error instanceof Error ? error : new Error(String(error)));
    }
    return () => {
        if (done)
            return;
        // `abort` fires `onabort`, which settles it.
        request.abort();
    };
}
/**
 * One event's worth of lines.
 *
 * Comments — a line starting with `:` — are the protocol's own no-op and
 * are dropped. A chunk with no `data:` at all is not an event; returning
 * null rather than an empty one keeps that out of the caller's handler.
 */
export function parseEvent(chunk) {
    let type = 'message';
    let id;
    const data = [];
    for (const line of chunk.split('\n')) {
        if (!line || line.startsWith(':'))
            continue;
        const colon = line.indexOf(':');
        const field = colon === -1 ? line : line.slice(0, colon);
        // One optional space after the colon belongs to the format, not the value.
        const value = colon === -1 ? '' : line.slice(colon + 1).replace(/^ /, '');
        if (field === 'event')
            type = value;
        else if (field === 'data')
            data.push(value);
        else if (field === 'id')
            id = value;
    }
    return data.length > 0 ? { type, data: data.join('\n'), id } : null;
}
